<template>
  <div class="page-shell">
    <section class="container">
      <div class="page-intro">
        <p class="eyebrow">AI Score Analysis</p>
        <h1 class="page-title">成绩分析</h1>
        <p class="page-description">
          基于 AI 的智能成绩分析报告
        </p>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="status-card loading-card">
        <div class="loading-spinner"></div>
        <p>AI 正在分析成绩，请稍候...</p>
        <small>首次分析可能需要 10-30 秒</small>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="errorMessage" class="status-card is-error">
        <p>{{ errorMessage }}</p>
        <button class="btn btn--primary" @click="loadAnalysis">重试</button>
      </div>

      <!-- 成绩分析内容 -->
      <template v-else>
        <!-- 成绩概览 -->
        <section class="panel overview-panel">
          <h2 class="section-title">成绩概览</h2>
          <div class="score-display">
            <div class="score-main">
              <span class="score-value">{{ analysis.score_overview.score }}</span>
              <span class="score-divider">/</span>
              <span class="score-total">{{ analysis.score_overview.total_score }}</span>
            </div>
            <div class="score-rate">得分率 {{ analysis.score_overview.score_rate }}%</div>
            <div
              class="level-badge"
              :class="getLevelClass(analysis.score_overview.performance_level)"
            >
              {{ analysis.score_overview.performance_level }}
            </div>
          </div>
          <div class="overview-stats">
            <div class="stat-item">
              <span class="stat-label">考试结果</span>
              <span
                class="stat-value"
                :class="getResultClass(analysis.score_overview.result)"
              >
                {{ analysis.score_overview.result_word }}
              </span>
            </div>
            <div class="stat-item">
              <span class="stat-label">及格分</span>
              <span class="stat-value">{{ analysis.score_overview.pass_score }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">距及格</span>
              <span class="stat-value">
                {{ analysis.score_overview.pass_gap > 0 ? `还差 ${analysis.score_overview.pass_gap} 分` : '已达标' }}
              </span>
            </div>
          </div>
        </section>

        <!-- 答题统计 -->
        <section class="panel stats-panel">
          <h2 class="section-title">答题统计</h2>
          <div class="stats-grid">
            <div class="grid-item">
              <span class="grid-value">{{ analysis.question_statistics.question_count }}</span>
              <span class="grid-label">题目总数</span>
            </div>
            <div class="grid-item success">
              <span class="grid-value">{{ analysis.question_statistics.correct_count }}</span>
              <span class="grid-label">答对</span>
            </div>
            <div class="grid-item error">
              <span class="grid-value">{{ analysis.question_statistics.incorrect_count }}</span>
              <span class="grid-label">答错/未答</span>
            </div>
            <div class="grid-item">
              <span class="grid-value">{{ analysis.question_statistics.accuracy_rate }}%</span>
              <span class="grid-label">正确率</span>
            </div>
          </div>
        </section>

        <!-- AI 总体评价 -->
        <section class="panel summary-panel">
          <h2 class="section-title">🤖 AI 总体评价</h2>
          <p class="summary-text">{{ analysis.ai_analysis.summary }}</p>
        </section>

        <!-- 优势表现 -->
        <section
          v-if="analysis.ai_analysis.strengths && analysis.ai_analysis.strengths.length"
          class="panel strengths-panel"
        >
          <h2 class="section-title">💪 优势表现</h2>
          <ul class="strength-list">
            <li v-for="(item, index) in analysis.ai_analysis.strengths" :key="index">
              <span class="bullet">✓</span>
              <span>{{ item }}</span>
            </li>
          </ul>
        </section>

        <!-- 薄弱点分析 -->
        <section
          v-if="analysis.ai_analysis.weaknesses && analysis.ai_analysis.weaknesses.length"
          class="panel weaknesses-panel"
        >
          <h2 class="section-title">📝 薄弱点分析</h2>
          <div class="weakness-list">
            <article
              v-for="(item, index) in analysis.ai_analysis.weaknesses"
              :key="index"
              class="weakness-item"
            >
              <div class="weakness-header">
                <span class="weakness-number">{{ index + 1 }}</span>
                <h3>{{ item.title }}</h3>
              </div>
              <p class="weakness-detail">{{ item.detail }}</p>
              <div v-if="item.question_ids && item.question_ids.length" class="question-ids">
                <span class="ids-label">相关题目：</span>
                <span class="ids-text">{{ item.question_ids.join(', ') }}</span>
              </div>
            </article>
          </div>
        </section>

        <!-- 学习建议 -->
        <section
          v-if="analysis.ai_analysis.study_suggestions && analysis.ai_analysis.study_suggestions.length"
          class="panel suggestions-panel"
        >
          <h2 class="section-title">💡 学习建议</h2>
          <div class="suggestion-list">
            <article
              v-for="(item, index) in analysis.ai_analysis.study_suggestions"
              :key="index"
              class="suggestion-item"
            >
              <div class="suggestion-header">
                <span class="priority-badge" :class="item.priority">
                  {{ getPriorityText(item.priority) }}
                </span>
                <h3>{{ item.title }}</h3>
              </div>
              <p class="suggestion-action">{{ item.action }}</p>
            </article>
          </div>
        </section>

        <!-- 重点错题分析 -->
        <section
          v-if="analysis.ai_analysis.question_reviews && analysis.ai_analysis.question_reviews.length"
          class="panel reviews-panel"
        >
          <h2 class="section-title">🎯 重点错题分析</h2>
          <div class="review-list">
            <article
              v-for="(item, index) in analysis.ai_analysis.question_reviews"
              :key="index"
              class="review-item"
            >
              <div class="review-header">
                <span class="question-label">题目 {{ item.question_id }}</span>
              </div>
              <div class="review-section">
                <span class="section-label">问题：</span>
                <p>{{ item.problem }}</p>
              </div>
              <div class="review-section">
                <span class="section-label">建议：</span>
                <p>{{ item.suggestion }}</p>
              </div>
            </article>
          </div>
        </section>

        <!-- 分析信息 -->
        <section class="panel meta-panel">
          <div class="meta-row">
            <span class="meta-label">分析模型：</span>
            <span class="meta-value">{{ analysis.model_name }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">分析时间：</span>
            <span class="meta-value">{{ analysis.analyzed_at }}</span>
          </div>
          <div v-if="analysis.questions_truncated === 1" class="meta-row">
            <span class="meta-tip">
              本次分析基于部分题目（共 {{ analysis.analyzed_question_count }} 题）
            </span>
          </div>
        </section>

        <!-- 返回按钮 -->
        <div class="action-bar">
          <button class="btn btn--secondary" @click="goBack">返回</button>
        </div>
      </template>
    </section>

    <!-- Token 失效弹窗 -->
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { fetchExamScoreAnalysis } from '@/api/courseProcess'
import AuthModal from '@/components/AuthModal.vue'
import authErrorHandler from '@/mixins/authErrorHandler'

export default {
  name: 'ScoreAnalysisView',
  components: {
    AuthModal
  },
  mixins: [authErrorHandler],
  data() {
    return {
      recordId: null,
      loading: false,
      errorMessage: '',
      analysis: {
        score_overview: {
          score: 0,
          total_score: 100,
          score_rate: 0,
          pass_score: 60,
          pass_gap: 0,
          result: 0,
          result_word: '',
          performance_level: ''
        },
        question_statistics: {
          question_count: 0,
          answered_count: 0,
          unanswered_count: 0,
          correct_count: 0,
          incorrect_count: 0,
          accuracy_rate: 0
        },
        ai_analysis: {
          summary: '',
          strengths: [],
          weaknesses: [],
          study_suggestions: [],
          question_reviews: []
        },
        model_name: '',
        analyzed_at: '',
        analyzed_question_count: 0,
        questions_truncated: 0
      }
    }
  },
  created() {
    this.recordId = this.$route.params.recordId
    if (!this.recordId) {
      this.errorMessage = '缺少考试记录ID'
      return
    }
    this.loadAnalysis()
  },
  methods: {
    async loadAnalysis() {
      this.loading = true
      this.errorMessage = ''

      try {
        const result = await fetchExamScoreAnalysis(this.recordId)
        this.analysis = result
      } catch (error) {
        console.error('加载成绩分析失败:', error)
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          this.errorMessage = error.message || '加载成绩分析失败'
          if (/正在分析中/.test(this.errorMessage)) {
            this.errorMessage = '成绩正在分析中，请稍后再试'
          }
        }
      } finally {
        this.loading = false
      }
    },
    getLevelClass(level) {
      if (level === '优秀') return 'excellent'
      if (level === '良好') return 'good'
      if (level === '合格') return 'pass'
      return 'weak'
    },
    getResultClass(result) {
      if (result === 1) return 'pass'
      if (result === 2) return 'fail'
      return ''
    },
    getPriorityText(priority) {
      if (priority === 'high') return '高优先级'
      if (priority === 'medium') return '中优先级'
      if (priority === 'low') return '低优先级'
      return priority
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="scss">
.loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 32px;

  p {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  small {
    color: $color-text-secondary;
    font-size: 14px;
  }
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba($color-primary, 0.2);
  border-top-color: $color-primary;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.panel {
  margin-bottom: 24px;
  padding: 28px;
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 700;
  color: $color-text-primary;
}

/* 成绩概览 */
.overview-panel {
  text-align: center;
}

.score-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
}

.score-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.score-value {
  font-size: 72px;
  font-weight: 800;
  color: $color-primary;
  line-height: 1;
}

.score-divider {
  font-size: 36px;
  color: $color-text-secondary;
}

.score-total {
  font-size: 48px;
  color: $color-text-secondary;
}

.score-rate {
  font-size: 16px;
  color: $color-text-secondary;
}

.level-badge {
  display: inline-block;
  padding: 8px 24px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;

  &.excellent {
    background: rgba(#10b981, 0.1);
    color: #10b981;
  }

  &.good {
    background: rgba(#3b82f6, 0.1);
    color: #3b82f6;
  }

  &.pass {
    background: rgba(#f59e0b, 0.1);
    color: #f59e0b;
  }

  &.weak {
    background: rgba(#ef4444, 0.1);
    color: #ef4444;
  }
}

.overview-stats {
  display: flex;
  justify-content: space-around;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid $color-border;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.stat-label {
  font-size: 14px;
  color: $color-text-secondary;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: $color-text-primary;

  &.pass {
    color: #10b981;
  }

  &.fail {
    color: #ef4444;
  }
}

/* 答题统计 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  border-radius: 16px;
  background: rgba($color-border, 0.2);

  &.success {
    background: rgba(#10b981, 0.1);

    .grid-value {
      color: #10b981;
    }
  }

  &.error {
    background: rgba(#ef4444, 0.1);

    .grid-value {
      color: #ef4444;
    }
  }
}

.grid-value {
  font-size: 36px;
  font-weight: 800;
  color: $color-text-primary;
}

.grid-label {
  font-size: 14px;
  color: $color-text-secondary;
}

/* AI 总体评价 */
.summary-text {
  margin: 0;
  color: $color-text-secondary;
  font-size: 16px;
  line-height: 1.8;
}

/* 优势表现 */
.strength-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 12px;
    background: rgba(#10b981, 0.05);

    .bullet {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: #10b981;
      color: white;
      font-size: 14px;
      font-weight: 700;
    }

    span:last-child {
      flex: 1;
      color: $color-text-secondary;
      line-height: 1.6;
    }
  }
}

/* 薄弱点分析 */
.weakness-list {
  display: grid;
  gap: 16px;
}

.weakness-item {
  padding: 20px;
  border-radius: 16px;
  background: rgba(#f59e0b, 0.05);
  border: 1px solid rgba(#f59e0b, 0.2);
}

.weakness-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .weakness-number {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #f59e0b;
    color: white;
    font-size: 14px;
    font-weight: 700;
  }

  h3 {
    flex: 1;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: $color-text-primary;
  }
}

.weakness-detail {
  margin: 0 0 12px 0;
  color: $color-text-secondary;
  line-height: 1.6;
}

.question-ids {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  .ids-label {
    color: $color-text-secondary;
    font-weight: 600;
  }

  .ids-text {
    color: $color-text-secondary;
  }
}

/* 学习建议 */
.suggestion-list {
  display: grid;
  gap: 16px;
}

.suggestion-item {
  padding: 20px;
  border-radius: 16px;
  background: rgba($color-primary, 0.05);
  border: 1px solid rgba($color-primary, 0.2);
}

.suggestion-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  h3 {
    flex: 1;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: $color-text-primary;
  }
}

.priority-badge {
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;

  &.high {
    background: rgba(#ef4444, 0.1);
    color: #ef4444;
  }

  &.medium {
    background: rgba(#f59e0b, 0.1);
    color: #f59e0b;
  }

  &.low {
    background: rgba(#10b981, 0.1);
    color: #10b981;
  }
}

.suggestion-action {
  margin: 0;
  color: $color-text-secondary;
  line-height: 1.6;
}

/* 重点错题分析 */
.review-list {
  display: grid;
  gap: 16px;
}

.review-item {
  padding: 20px;
  border-radius: 16px;
  background: rgba($color-border, 0.2);
  border-left: 4px solid $color-primary;
}

.review-header {
  margin-bottom: 16px;

  .question-label {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 8px;
    background: $color-primary;
    color: white;
    font-size: 13px;
    font-weight: 600;
  }
}

.review-section {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .section-label {
    display: block;
    margin-bottom: 4px;
    color: $color-text-secondary;
    font-size: 14px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: $color-text-secondary;
    line-height: 1.6;
  }
}

/* 分析信息 */
.meta-panel {
  background: rgba($color-border, 0.1);
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.meta-label {
  color: $color-text-secondary;
  font-size: 14px;
}

.meta-value {
  color: $color-text-primary;
  font-size: 14px;
}

.meta-tip {
  color: #f59e0b;
  font-size: 14px;
  font-style: italic;
}

/* 操作栏 */
.action-bar {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

/* 响应式 */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .overview-stats {
    flex-direction: column;
    gap: 16px;
  }

  .score-value {
    font-size: 56px;
  }

  .score-total {
    font-size: 36px;
  }
}
</style>
